<template>
    <div v-if="step">
        <div class="row">
            <div class="col-xl-6 m-b-20">
                <!-- begin stage card -->
                <b-card bg-variant="dark" footer-bg-variant="dark" text-variant="light" :title="step.name" :img-src="getImage(step.image)" img-alt="Image" img-top>
                    <div class="p-l-5 p-r-5 m-b-15">
                        <p class="card-text">{{ step.description }}</p>
                    </div>
                </b-card>
                <!-- end stage card -->
            </div>
            <div class="col-xl-6">
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Details</h6>
                    </template>
                    <b-card-text>
                        <b>Name: </b><span>{{ step.name }}</span><br>
                        <b>Stage: </b><span>{{ stage ? stage.name : "-" }}</span><br>
                        <b>Storyboard: </b><span>{{ storyboard ? storyboard.name : "-" }}</span><br>
                        <b>Insights: </b><span>{{ step.insights ? step.insights : "-" }}</span><br>
                        <b>Target outcome: </b><span>{{ step.target ? step.target : "-" }}</span><br>
                        <b>Responsible person: </b><span>{{ step.responsiblePerson ? step.responsiblePerson : "-" }}</span><br>
                        <b>Planned things to provide: </b><span>{{ step.plan ? step.plan : "-" }}</span><br>
                        <b>Production budget: </b><span>{{ step.budget ? `${step.budget} €` : "-" }}</span><br>
                    </b-card-text>
                </b-card>
                <!-- begin panel -->
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Script data</h6>
                    </template>
                    <b-card-text>
                        <div>
                            <b>Total active items: </b><span>{{ sItems.length }}</span><br>
                            <b>Active space items: </b><span>{{ filterItemsByType('space').length }}</span><br>
                            <b>Active stuff items: </b><span>{{ filterItemsByType('stuff').length }}</span><br>
                            <b>Active staff items: </b><span>{{ filterItemsByType('staff').length }}</span><br>
                            <b>Active service items: </b><span>{{ filterItemsByType('service').length }}</span>
                        </div>
                    </b-card-text>
                </b-card>
                <!-- end panel -->
            </div>
        </div>
        <div class="row">
            <div class="col-xl-6">
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Files</h6>
                    </template>
                    <b-card-text>
                        <vue-custom-scrollbar class="height-sm">
                            <div class="row">
                                <div class="m-20 text-center" v-for="file in step.files" :key="file.id">
                                    <a :href="fileUrl + file.fileFd" :download="file.name" target="_blank">
                                        <i v-if="fileType(file.fileFd) == 'pdf'" class="fas fa-file-pdf fa-5x" />
                                        <i v-else-if="fileType(file.fileFd) == ('jpg' || 'jpeg')" class="fas fa-file-image fa-5x" />
                                        <p class="text-black">{{ file.name }}</p>
                                    </a>
                                </div>
                            </div>
                            <div v-if="!step.files.length">
                                <p class="m-l-10">No files</p>
                            </div>
                        </vue-custom-scrollbar>
                    </b-card-text>
                </b-card>
            </div>
            <div class="col-xl-6">
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Notes</h6>
                    </template>
                    <b-card-text>
                        <vue-custom-scrollbar class="height-sm">
                            <ul class="media-list media-list-with-divider media-messaging">
                                <li class="media media-sm" v-for="(note, index) in step.notes" :key="note.id">
                                    <div class="media-body">
                                        <h5 class="media-heading">Note #{{ index + 1 }}</h5>
                                        <p>{{ note.content }}</p>
                                    </div>
                                </li>
                                <li v-if="!step.notes.length" class="media media-sm">
                                    <div class="media-body">
                                        <p>No notes</p>
                                    </div>
                                </li>
                            </ul>
                        </vue-custom-scrollbar>
                    </b-card-text>
                </b-card>
            </div>
        </div>
        <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
            <template v-slot:header>
                <h6 class="mb-0">Actions</h6>
            </template>
            <url-button v-if="!modalMode" class="btn-secondary" label="Back" :url="step.stage ? `/storyboard/${$route.params.id}/stage/${step.stage}` : `/storyboard`" />
            <url-button class="btn-white pull-right" label="Edit step" :url="`/storyboard/${storyboard ? storyboard.id : null}/stage/${step.stage}/step/${step.id}/edit`" />
            <b-button  v-if="!modalMode" v-b-modal.modalCopy class="btn-white pull-right mr-1">Make a copy</b-button>
        </b-card>

        <b-modal id="modalCopy" centered title="Make a copy" cancel-variant="white" ok-variant="yellow" ok-title="Confirm" v-on:ok="copy" :ok-disabled="disabled">
            <div class="alert alert-secondary m-b-0">
                <h5><i class="fa fa-info-circle"></i>Make a copy</h5>
                <p>Are you sure you want to make a copy of {{ step.name }}?</p>
                <div class="form-group row m-b-15">
                    <label class="col-form-label col-md-3"><b>Name:</b></label>
                    <div class="col-md-9">
                        <input @input="buttonStatus" v-model="copyName" type="text" class="form-control m-b-5" placeholder="Enter new name" />
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import UrlButton from '../../components/buttons/UrlButton.vue'

export default {
    name: 'StepShow',
    props: ['step', 'stage', 'storyboard', 'scriptItems', 'modalMode', 'activeEvent'],
    components: {
        UrlButton,
    },
    data() {
        return {
            sItems: [],
            copyName: null,
            disabled: false,
            fileUrl: process.env.VUE_APP_API_URL + '/uploads/files/'
        }
    },
    methods: {
        getImage(image) {
            if (image == null) {
                return require('../../assets/images/no_image.jpg');
            } else {
                return process.env.VUE_APP_API_URL + '/uploads/images/' + image.imageFd;
            }
        },
        getTypeName(typeId) {
            switch (typeId) {
                case 1:
                    return 'service'
                case 2:
                    return 'space'
                case 3:
                    return 'staff'
                case 4:
                    return 'stuff'
                default:
                    return ''
            }
        },
        fileType(path) {
           return path.slice(path.lastIndexOf('.') + 1);
        },
        filterItems(step) {
            let filteredItems = this.scriptItems.filter(sItem => {
                if (step.id == sItem.step.id) {
                    return sItem;
                }
            });
            this.sItems = filteredItems;
        },
        filterItemsByType(type) {
            return this.sItems.filter(sItem => {
                if (this.getTypeName(sItem.item.type) == type) {
                    return sItem;
                }
            });
        },
        buttonStatus() {
            if (this.copyName == '' || this.copyName == null) {
                this.disabled = true;
            } else {
                this.disabled = false;
            }
        },
        copy() {
            let imageId = null,
                fileIds = [];
            if (this.step.image) {
                imageId = this.step.image.id;
            }
            this.step.files.forEach(file => {
                fileIds.push(file.id);
            });
            this.$parent.$parent.stepCreate(JSON.stringify({
                stage: this.step.stage,
                name: this.copyName,
                description: this.step.description,
                imageId: imageId,
                fileIds: fileIds,
                notes: this.step.notes,
                eventId: parseInt(this.activeEvent.id)
            }));
        }
    },
    mounted() {
        this.filterItems(this.step);
    },
    watch: {
        step: function () {
            this.filterItems(this.step);
        }
    }
}
</script>